// V2 Person / Prospect sheet — one unified page with two tabs.
//
// Two entry contexts, one page (matches the "5" wireframe):
//   - From approval queue (Today "Messages waiting", Board "you" banner) → lands on Message
//   - From pipeline board / campaign People / Today reply row → lands on Activity
//
// Message tab: MESSAGE · EDITABLE + Skip / Edit / Approve & send. Only rendered
// when the lead genuinely needs approval (stage='prospect' AND !approvedAt AND !archived).
// Approving or skipping advances to the next unapproved lead in the same campaign
// (silent queue — no visible progress bar; navigation is the queue).
//
// Activity tab: TIMELINE synthesised from what we know (added / approved / note-added).
//
// Right column stays across both tabs: WHY HIM card + editable COACHING NOTES with
// an explicit Save button (each save also logs a `note` activity that shows up in
// the Activity tab timeline).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconArrowRight,
  IconBrandLinkedin,
  IconExternalLink,
  IconEye,
  IconMail,
  IconMessage,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  PUBLIC_PAGE_BASE_URL,
  fetchActivities,
  fetchArticles,
  fetchCampaign,
  fetchLead,
  fetchLeadEvents,
  fetchLeadPlan,
  fetchLeadTasks,
  fetchLeads,
  fetchMyPrincipal,
  generateIntroDraft,
  patchTask,
  pingConnectedAutoNextTask,
  shareArticleWithLead,
  updateLead,
} from '../services/api';
import { substituteTokens } from '../v2/messageTokens';
import { normalizeConnectedLaneMode } from '../lib/laneAutomation';
import {
  getIntroProposalState,
  mergeDraftIntoLead,
} from '../lib/introEligibility';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function firstNameOf(name) {
  return String(name || '').trim().split(/\s+/)[0] || '';
}

function fitColorFor(score) {
  const v = Number(score || 0);
  if (v >= 80) return 'var(--mantine-color-sage-6)';
  if (v >= 60) return 'var(--mantine-color-sage-4)';
  if (v >= 40) return 'var(--aviary-muted)';
  return 'var(--aviary-disabled)';
}

function FitCircle({ score }) {
  const v = Math.round(Number(score || 0));
  if (!v) return null;
  const color = fitColorFor(v);
  return (
    <Box
      style={{
        width: 68,
        height: 68,
        borderRadius: '50%',
        border: `3px solid ${color}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Text fw={700} size="xl" style={{ color: 'var(--aviary-ink)', lineHeight: 1 }}>
        {v}
      </Text>
    </Box>
  );
}

function domainOf(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const withScheme = raw.startsWith('http') ? raw : `https://${raw}`;
  try {
    return new URL(withScheme).hostname.replace(/^www\./, '') || raw;
  } catch (_err) {
    return raw.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
}

function ContactChip({ Icon, label, href }) {
  return (
    <UnstyledButton
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        borderRadius: 6,
        border: '1px solid var(--aviary-disabled)',
        color: 'var(--aviary-ink)',
        background: 'transparent',
        fontSize: 12.5,
      }}
    >
      <Icon size={14} />
      <Text size="xs" fw={500}>{label}</Text>
    </UnstyledButton>
  );
}

function stagePillLabel(stage) {
  const s = String(stage || '').toLowerCase();
  if (!s) return '—';
  if (s === 'clicked') return 'Clicked';
  if (s === 'sent') return 'Sent';
  if (s === 'connected') return 'Connected';
  if (s === 'curious') return 'Curious';
  if (s === 'warm') return 'Warm';
  if (s === 'hot') return 'Hot';
  if (s === 'article_opened') return 'Warm';
  if (s === 'nurturing') return 'Curious';
  if (s === 'replying') return 'Hot';
  if (s === 'qualified') return 'Queued';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function humanizeDraftGuardError(err) {
  const code = String(err?.response?.data?.code || '').trim();
  if (code === 'BLACKBOX_AGENT_ONLY_DRAFTS') {
    return 'This is an agent-managed campaign. Strategist creates and sends these drafts automatically.';
  }
  return err?.response?.data?.error || err?.message || 'Something went wrong.';
}

function channelLabelOf(channel) {
  const c = String(channel || '').toLowerCase();
  if (c === 'linkedin') return 'LinkedIn';
  if (c === 'email') return 'Email';
  if (c === 'whatsapp') return 'WhatsApp';
  if (c === 'sms') return 'SMS';
  return c || '';
}

function formatAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const diff = Date.now() - then;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hrs = Math.floor(diff / (60 * 60 * 1000));
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  if (hrs > 0) return `${hrs}h ago`;
  return 'just now';
}

// Absolute datetime for audit-trail timestamps. Includes year so old events
// stay unambiguous. Example: "17 Aug 2026 · 3:42 pm".
export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} · ${time.toLowerCase()}`;
}

function truncate(text, max = 120) {
  const s = String(text || '').trim();
  return s.length <= max ? s : `${s.slice(0, max).trimEnd()}…`;
}

function formatDwell(seconds) {
  const v = Number(seconds);
  if (!Number.isFinite(v) || v <= 0) return '';
  const rounded = Math.round(v);
  const mins = Math.floor(rounded / 60);
  const rem = rounded % 60;
  return mins > 0 ? `${mins}m ${rem}s` : `${rem}s`;
}

function formatReadPct(pct) {
  const n = Number(pct);
  if (!Number.isFinite(n) || n < 0) return '';
  return `${Math.max(0, Math.min(100, Math.round(n)))}% read`;
}

function formatLocationLabel(location) {
  if (!location || typeof location !== 'object') return '';
  const parts = [
    String(location.city || '').trim(),
    String(location.region || '').trim(),
    String(location.country || '').trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

function formatUserAgent(ua) {
  const text = String(ua || '').trim();
  return text ? `UA: ${text}` : '';
}

function inferArticleSlugFromUrl(destinationUrl, principalId) {
  const raw = String(destinationUrl || '').trim();
  const pid = String(principalId || '').trim();
  if (!raw || !pid) return null;
  const marker = `/p/${pid}/`;
  const idx = raw.indexOf(marker);
  if (idx < 0) return null;
  const tail = raw.slice(idx + marker.length);
  const slug = tail.split(/[/?#]/)[0];
  return slug ? decodeURIComponent(slug) : null;
}

function articlePublicUrl(principalId, articleSlug) {
  const pid = String(principalId || '').trim();
  const slug = String(articleSlug || '').trim();
  if (!pid || !slug) return '';
  return `${PUBLIC_PAGE_BASE_URL}/p/${encodeURIComponent(pid)}/${encodeURIComponent(slug)}`;
}

export function shortActorToken(sub) {
  const value = String(sub || '').trim();
  if (!value) return '';
  const parts = value.split('|');
  if (parts.length >= 2) {
    const rest = parts.slice(1).join('|');
    return `${parts[0]}|${rest.slice(0, 6)}${rest.length > 6 ? '...' : ''}`;
  }
  return `${value.slice(0, 12)}${value.length > 12 ? '...' : ''}`;
}

export function actorBadgeLabel(actorDisplay, actorSub) {
  const display = String(actorDisplay || '').trim();
  if (display && display.toLowerCase() !== 'system') return display;
  const sub = String(actorSub || '').trim();
  if (!sub || sub.toLowerCase() === 'system') return '';
  return shortActorToken(sub);
}

const CHANNEL_MODE_DEFAULTS = {
  email: 2,
  linkedin: 0,
  whatsapp: 0,
  sms: 0,
  wechat: 0,
};

function mergedChannelModes(raw) {
  const merged = { ...CHANNEL_MODE_DEFAULTS };
  if (!raw || typeof raw !== 'object') return merged;
  for (const channel of Object.keys(CHANNEL_MODE_DEFAULTS)) {
    const n = Number(raw[channel]);
    if ([0, 1, 2].includes(n)) merged[channel] = n;
  }
  return merged;
}

function isPendingArticleDraft(draft) {
  return !draft?.approvedAt && !draft?.skippedAt && !draft?.sentAt;
}

function hasSentArticleShareDraft(lead) {
  return Array.isArray(lead?.articleDrafts) && lead.articleDrafts.some((draft) =>
    draft?.source === 'article-share' && draft?.approvedAt
  );
}

function isConnectedQueueStage(stage) {
  const s = String(stage || '').toLowerCase();
  return s === 'connected'
    || s === 'warm'
    || s === 'curious'
    || s === 'nurturing'
    || s === 'article_opened'
    || s === 'replying';
}

function isIntroMessageDraft(draft) {
  return String(draft?.source || '').toLowerCase() === 'intro-message';
}

function isArticleLinkSource(source) {
  const s = String(source || '').toLowerCase();
  return s === 'article-share'
    || s === 'article-promo'
    || s === 'article-reactivation'
    || s === 'pipeline-share'
    || s === 'connected-auto';
}

function articleDraftQueueTs(draft) {
  const ts = Date.parse(draft?.preparedAt || draft?.createdAt || '');
  return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
}

// ─── Timeline builder ────────────────────────────────────────────────────────
export function buildSynthesizedTimeline({ lead, campaign, events, channelLabel, noteActivities }) {
  const entries = [];
  if (!lead) return entries;

  if (lead.createdAt) {
    const creatorDisplay = String(lead.createdByDisplay || '').trim();
    if (creatorDisplay) {
      entries.push({
        key: 'added-by',
        ts: lead.createdAt,
        title: `${creatorDisplay} added this prospect`,
        detail: '',
        filled: false,
        showActorBadge: false,
      });
    } else {
      entries.push({
        key: 'added',
        ts: lead.createdAt,
        title: `Added from ${campaign?.name || 'campaign'}`,
        detail: '',
        filled: false,
        showActorBadge: false,
      });
    }
  }
  if (lead.approvedAt) {
    entries.push({
      key: 'approved',
      ts: lead.approvedAt,
      title: `Opener approved${channelLabel ? ` · ${channelLabel}` : ''}`,
      detail: 'you approved it',
      body: String(lead.approvedMessage || '').trim() || null,
      filled: true,
      showActorBadge: false,
    });
  }
  // Article and intro drafts.
  const drafts = Array.isArray(lead.articleDrafts) ? lead.articleDrafts : [];
  const draftsByLinkId = new Map();
  drafts.forEach((draft) => {
    const linkId = String(draft?.linkId || '').trim();
    if (linkId && !draftsByLinkId.has(linkId)) draftsByLinkId.set(linkId, draft);
  });
  drafts.forEach((draft, i) => {
    const source = String(draft?.source || '').toLowerCase();
    const draftKey = draft?.id || draft?.linkId || i;
    // Article send/click/read/forward rows are rendered from magicLinks below
    // so we can include link analytics context and stable title hyperlinks.
    if (isArticleLinkSource(source)) return;
    if (draft?.sentAt) {
      entries.push({
        key: `draft-sent-${draftKey}`,
        ts: draft.sentAt,
        title: source === 'intro-message' ? 'Intro sent' : 'Message sent',
        detail: '',
        body: String(draft.messageBody || '').trim() || null,
        filled: true,
        actorSub: String(draft.createdBy || '').trim() || null,
        actorDisplay: String(draft.createdByDisplay || '').trim() || null,
        showActorBadge: true,
      });
      return;
    }
    // Terminal-but-not-sent marker surfaces failed/skipped states that were
    // previously invisible — the "Draft approved" entry alone looked like
    // the draft was still waiting for delivery. failedAt takes precedence
    // over skippedAt because skip is usually a recovery action after fail;
    // the user wants to know "the system couldn't deliver this".
    if (draft?.failedAt) {
      const reason = String(draft.failureReason || '').trim();
      entries.push({
        key: `draft-failed-${draftKey}`,
        ts: draft.failedAt,
        title: source === 'intro-message' ? 'Intro delivery failed' : 'Draft delivery failed',
        detail: reason ? `reason: ${reason}` : '',
        filled: false,
        actorSub: String(draft.createdBy || '').trim() || null,
        actorDisplay: String(draft.createdByDisplay || '').trim() || null,
        showActorBadge: true,
      });
    } else if (draft?.skippedAt) {
      const reason = String(draft.skippedReason || '').trim();
      entries.push({
        key: `draft-skipped-${draftKey}`,
        ts: draft.skippedAt,
        title: source === 'intro-message' ? 'Intro draft skipped' : 'Draft skipped',
        detail: reason,
        filled: false,
        actorSub: String(draft.createdBy || '').trim() || null,
        actorDisplay: String(draft.createdByDisplay || '').trim() || null,
        showActorBadge: true,
      });
    }
    if (draft?.approvedAt) {
      const title = source === 'intro-message' ? 'Intro message approved' : 'Draft approved';
      entries.push({
        key: `draft-${draftKey}`,
        ts: draft.approvedAt,
        title,
        detail: draft.articleTitle || draft.articleSlug || '',
        body: String(draft.messageBody || '').trim() || null,
        filled: true,
        actorSub: String(draft.createdBy || '').trim() || null,
        actorDisplay: String(draft.createdByDisplay || '').trim() || null,
        showActorBadge: true,
      });
    }
  });

  const articleMagicLinkIds = new Set(
    (lead.magicLinks || [])
      .filter((item) => isArticleLinkSource(item?.source))
      .map((item) => String(item?.linkId || '').trim())
      .filter(Boolean)
  );
  const magicLinkById = new Map(
    (lead.magicLinks || [])
      .map((item) => [String(item?.linkId || '').trim(), item])
      .filter(([lid]) => Boolean(lid))
  );
  // pageVisits index — retroactive enrichment for events written before
  // v2.23.0 payload denormalization. Primary join key is visitId (present
  // on recent dwell/read/click payloads). Fallback: match by linkId +
  // closest-timestamp for historical events that predate visitId capture.
  const leadPageVisits = Array.isArray(lead.pageVisits) ? lead.pageVisits : [];
  const pageVisitById = new Map(
    leadPageVisits
      .map((v) => [String(v?.visitId || '').trim(), v])
      .filter(([id]) => Boolean(id))
  );
  const pageVisitsByLinkId = new Map();
  for (const v of leadPageVisits) {
    const lid = String(v?.linkId || '').trim();
    if (!lid) continue;
    if (!pageVisitsByLinkId.has(lid)) pageVisitsByLinkId.set(lid, []);
    pageVisitsByLinkId.get(lid).push(v);
  }
  function pickPageVisitByLinkIdAndTs(linkId, ts) {
    const list = pageVisitsByLinkId.get(String(linkId || '').trim());
    if (!list || !list.length) return null;
    const targetMs = new Date(ts || 0).getTime();
    if (!Number.isFinite(targetMs) || targetMs === 0) return list[0];
    let best = list[0];
    let bestDiff = Math.abs(new Date(best?.ts || 0).getTime() - targetMs);
    for (const v of list) {
      const diff = Math.abs(new Date(v?.ts || 0).getTime() - targetMs);
      if (diff < bestDiff) { best = v; bestDiff = diff; }
    }
    return best;
  }

  for (const ev of events || []) {
    const type = String(ev.type || '').toLowerCase();
    const evLinkId = String(ev.payload?.linkId || '').trim();
    const evLink = evLinkId ? magicLinkById.get(evLinkId) : null;
    const isArticleLink = Boolean(evLink && isArticleLinkSource(evLink?.source));
    const draftForLink = evLinkId ? draftsByLinkId.get(evLinkId) : null;
    const eventArticleTitle = String(
      draftForLink?.articleTitle
      || evLink?.destinationLabel
      || ev.payload?.articleTitle
      || ''
    ).trim();
    const eventArticleSlug = String(
      ev.payload?.articleSlug
      || evLink?.articleSlug
      || draftForLink?.articleSlug
      || ''
    ).trim() || inferArticleSlugFromUrl(evLink?.destinationUrl, lead.principalId) || '';
    const eventArticleHref = articlePublicUrl(lead.principalId, eventArticleSlug);
    // Fallback: if the event payload is missing UA/city/region/country (pre-
    // v2.23.0 events), look up the matching pageVisit. First by visitId
    // (present on recent events), else by linkId + closest event timestamp
    // (for historical events written before visitId was captured on payload).
    const evVisitId = String(ev.payload?.visitId || '').trim();
    const visitFallback = (evVisitId ? pageVisitById.get(evVisitId) : null)
      || (evLinkId ? pickPageVisitByLinkIdAndTs(evLinkId, ev.ts || ev.createdAt) : null);
    const eventLocation = formatLocationLabel({
      city: ev.payload?.city || visitFallback?.city,
      region: ev.payload?.region || visitFallback?.region,
      country: ev.payload?.country || visitFallback?.country,
    });
    const eventDevice = String(ev.payload?.device || visitFallback?.device || '').trim();
    const eventUa = formatUserAgent(ev.payload?.userAgent || visitFallback?.userAgent);

    if (type === 'click' || type === 'clicked') {
      const fallbackClickParts = [
        formatLocationLabel(evLink?.clickLocation),
        String(evLink?.clickDevice || '').trim(),
      ].filter(Boolean);
      const detailParts = [
        eventLocation,
        eventDevice,
        ...(isArticleLink ? ['on the Aviary-hosted article'] : []),
      ].filter(Boolean);
      const detail = detailParts.join(' · ') || fallbackClickParts.join(' · ');
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: isArticleLink ? 'Clicked' : 'Clicked your link',
        articleTitle: isArticleLink ? eventArticleTitle : '',
        articleHref: isArticleLink ? eventArticleHref : '',
        detail,
        ua: eventUa,
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    } else if (type === 'dwell') {
      const secs = Number(ev.payload?.elapsedSeconds || 0);
      const scroll = Number(ev.payload?.maxScrollPct || 0);
      const mins = Math.floor(secs / 60);
      const rem = secs % 60;
      const duration = mins > 0 ? `${mins}m ${rem}s` : `${rem}s`;
      const parts = [`Read ${duration}`];
      if (scroll > 0) parts.push(`scrolled to ${scroll}%`);
      const detailParts = [
        eventLocation,
        eventDevice,
        ...(isArticleLink ? ['on the Aviary-hosted article'] : []),
      ].filter(Boolean);
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: parts.join(' · '),
        articleTitle: isArticleLink ? eventArticleTitle : '',
        articleHref: isArticleLink ? eventArticleHref : '',
        detail: detailParts.join(' · '),
        ua: eventUa,
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    } else if (type === 'read') {
      if (evLinkId && articleMagicLinkIds.has(evLinkId)) continue;
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: 'Read your article',
        detail: '',
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    } else if (type === 'sent') {
      const isExtensionSend = ev.payload?.source === 'chrome-ext-invite';
      const isConnectedAutoSend = ev.payload?.source === 'connected-auto';
      const isArticleSend = Boolean(isArticleLink || eventArticleSlug);
      let title = 'Sent';
      const detailParts = [];
      if (isArticleSend) {
        title = 'Article sent';
      } else if (isExtensionSend || isConnectedAutoSend) {
        title = 'Sent using browser extension automation';
        if (ev.payload?.extensionVersion) detailParts.push(`extension v${ev.payload.extensionVersion}`);
        if (ev.payload?.installId) detailParts.push(`install ${String(ev.payload.installId).slice(0, 8)}`);
        const linkedInId = String(ev.payload?.linkedinProviderId || ev.payload?.linkedinId || '').trim();
        if (linkedInId) detailParts.push(`linkedin id ${linkedInId}`);
      } else if (ev.payload?.channel) {
        title = `Sent via ${channelLabelOf(ev.payload.channel)}`;
      }
      if (isArticleSend && (isExtensionSend || isConnectedAutoSend)) {
        if (ev.payload?.extensionVersion) detailParts.push(`extension v${ev.payload.extensionVersion}`);
        if (ev.payload?.installId) detailParts.push(`install ${String(ev.payload.installId).slice(0, 8)}`);
        const linkedInId = String(ev.payload?.linkedinProviderId || ev.payload?.linkedinId || '').trim();
        if (linkedInId) detailParts.push(`linkedin id ${linkedInId}`);
      }
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title,
        articleTitle: isArticleSend ? eventArticleTitle : '',
        articleHref: isArticleSend ? eventArticleHref : '',
        detail: detailParts.join(' · '),
        body: isArticleSend
          ? (String(draftForLink?.messageBody || '').trim() || null)
          : null,
        filled: true,
        actorSub: String(ev.actor || '').trim() || null,
        actorDisplay: String(ev.actorDisplay || '').trim() || null,
        showActorBadge: true,
        systemBadge: String(ev.actor || '').trim().toLowerCase() === 'system',
      });
    } else if (type === 'advance' && ev.payload) {
      // Skip advance-to-sent events; article sends render from articleDrafts
      // above with the actual message body attached.
      const to = String(ev.payload.to || '').toLowerCase();
      if (to === 'sent') continue;
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: `Advanced to ${stagePillLabel(ev.payload.to)}`,
        detail: ev.payload.reason || '',
        filled: false,
        actorSub: String(ev.actor || '').trim() || null,
        actorDisplay: String(ev.actorDisplay || '').trim() || null,
        showActorBadge: true,
        systemBadge: String(ev.actor || '').trim().toLowerCase() === 'system',
      });
    } else if (type === 'booked') {
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: 'Booked',
        detail: 'you marked this',
        filled: true,
        showActorBadge: false,
      });
    } else if (type === 'opt_out' || (type === 'episode_closed' && ev.payload?.reason === 'opted_out')) {
      const source = type === 'opt_out' ? (ev.payload?.source || 'manual') : (ev.payload?.source || 'manual');
      const sourceLabel = {
        stop_word: 'Replied with stop word',
        'url-click': 'Clicked unsubscribe link',
        manual: 'Manually opted out',
      }[String(source)] || 'Opted out';
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: 'Opted out',
        detail: sourceLabel,
        body: ev.payload?.triggerText || null,
        filled: true,
        optOut: true,
        showActorBadge: false,
      });
    } else if (type === 'reply') {
      const intent = String(ev.payload?.intent || '').trim().toLowerCase();
      const confidence = Number(ev.payload?.confidence || 0);
      const replyChannel = String(ev.payload?.channel || ev.payload?.source || '').toLowerCase();
      const channelLabel = replyChannel === 'sms' || replyChannel === 'twilio' ? 'via SMS' : 'via LinkedIn DM';
      const detailParts = [channelLabel];
      if (intent && intent !== 'other') {
        detailParts.push(confidence > 0
          ? `intent: ${intent} (${Math.round(confidence * 100)}%)`
          : `intent: ${intent}`);
      }
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: 'Replied',
        detail: detailParts.join(' · '),
        body: String(ev.payload?.text || '').trim() || null,
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    } else if (type === 'convo_state_changed') {
      const to = String(ev.payload?.to || '').trim().toLowerCase();
      const label = to === 'awaiting_principal'
        ? 'Conversation: your turn to reply'
        : to === 'awaiting_them'
          ? 'Conversation: awaiting their reply'
          : (to ? `Conversation: ${to.replace(/_/g, ' ')}` : '');
      if (!label) continue;
      entries.push({
        key: `ev-${ev.id}`,
        ts: ev.ts || ev.createdAt,
        title: label,
        detail: '',
        filled: false,
        showActorBadge: false,
      });
    }
  }

  // Magic-link engagement (sent / clicked / read / forwards), including article
  // title hyperlinks and denormalized geo/device context.
  const renderedClickLinkIds = new Set();
  const renderedReadLinkIds = new Set();
  const renderedSentLinkIds = new Set();
  for (const ev of events || []) {
    const t = String(ev.type || '').toLowerCase();
    const lid = String(ev.payload?.linkId || '');
    if (!lid) continue;
    if (t === 'click' || t === 'clicked') renderedClickLinkIds.add(lid);
    if (t === 'read' || t === 'dwell') renderedReadLinkIds.add(lid);
    if (t === 'sent') renderedSentLinkIds.add(lid);
  }
  const magicLinks = Array.isArray(lead.magicLinks) ? lead.magicLinks : [];
  magicLinks.forEach((link, i) => {
    const linkId = String(link?.linkId || '');
    const source = String(link?.source || '').toLowerCase();
    if (!isArticleLinkSource(source)) return;
    const matchingDraft = draftsByLinkId.get(linkId) || null;
    const articleTitle = String(matchingDraft?.articleTitle || link?.destinationLabel || '').trim() || 'article';
    const articleSlug = String(link?.articleSlug || matchingDraft?.articleSlug || '').trim()
      || inferArticleSlugFromUrl(link?.destinationUrl, lead.principalId)
      || '';
    const articleHref = articlePublicUrl(lead.principalId, articleSlug);

    if (link?.sentAt && !(linkId && renderedSentLinkIds.has(linkId))) {
      entries.push({
        key: `ml-sent-${linkId || i}`,
        ts: link.sentAt,
        title: 'Article sent',
        articleTitle,
        articleHref,
        detail: '',
        body: String(matchingDraft?.messageBody || '').trim() || null,
        filled: true,
        actorSub: String(matchingDraft?.createdBy || '').trim() || null,
        actorDisplay: String(matchingDraft?.createdByDisplay || '').trim() || null,
        showActorBadge: true,
      });
    }

    if (link?.clickedAt && !(linkId && renderedClickLinkIds.has(linkId))) {
      const clickParts = [
        formatLocationLabel(link?.clickLocation),
        String(link?.clickDevice || '').trim(),
      ].filter(Boolean);
      entries.push({
        key: `ml-click-${linkId || i}`,
        ts: link.clickedAt,
        title: 'Clicked',
        articleTitle,
        articleHref,
        detail: clickParts.join(' · '),
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    }
    if (link?.readAt && !(linkId && renderedReadLinkIds.has(linkId))) {
      entries.push({
        key: `ml-read-${linkId || i}`,
        ts: link.readAt,
        title: 'Read',
        articleTitle,
        articleHref,
        detail: [
          formatLocationLabel(link?.readLocation),
          String(link?.readDevice || '').trim(),
          formatDwell(link?.readDwellSeconds),
          formatReadPct(link?.readScrollPct),
        ].filter(Boolean).join(' · '),
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    }
    const forwards = Number(link?.forwardedReaderCount || 0);
    if (forwards > 0) {
      entries.push({
        key: `ml-fwd-${linkId || i}`,
        ts: link.lastActivityAt || link.readAt || link.clickedAt || link.sentAt,
        title: 'Forwarded read on',
        articleTitle,
        articleHref,
        detail: `${forwards} forward${forwards === 1 ? '' : 's'}`,
        side: 'left',
        filled: true,
        showActorBadge: false,
      });
    }
  });

  const noteBodies = new Set();
  for (const act of noteActivities || []) {
    if (String(act.type || '').toLowerCase() !== 'note') continue;
    const body = String(act.body || act.summary || '').trim();
    if (!body) continue;
    noteBodies.add(body);
    entries.push({
      key: `note-${act.id}`,
      ts: act.createdAt,
      title: 'Coaching note added',
      detail: truncate(body),
      filled: true,
      actorSub: String(act.actorSub || act.actor || '').trim() || null,
      actorDisplay: String(act.performedBy || act.author || '').trim() || null,
      showActorBadge: true,
    });
  }
  // Coaching notes stored on the lead but never logged as activities (older data).
  const coachNotes = Array.isArray(lead.coachingNotes) ? lead.coachingNotes : [];
  coachNotes.forEach((note, i) => {
    const body = String(note?.body || '').trim();
    if (!body || noteBodies.has(body)) return;
    entries.push({
      key: `cnote-${note.id || i}`,
      ts: note.createdAt,
      title: 'Coaching note added',
      detail: truncate(body),
      filled: true,
      actorDisplay: String(note.author || '').trim() || null,
      showActorBadge: true,
    });
  });
  return entries.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
}

// Message tab — pure conversation flow (inbound replies + outbound sent
// messages only, no system events). Same chat-bubble styling as the Activity
// timeline. Newest at top, oldest at bottom — matches the Activity tab's
// reverse-chron order so Jackie sees the most recent exchange first without
// scrolling. Pending drafts (approved-not-sent) are intentionally hidden so
// the view reads as a clean exchange, not a workflow queue.
function MessageFlowCard({ entries }) {
  const flow = (entries || [])
    .filter((e) => {
      if (!e || !e.body) return false;
      const t = String(e.title || '');
      return /Replied$|Sent$|Intro sent$|Message sent$/i.test(t);
    })
    .slice()
    .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  if (!flow.length) {
    return (
      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>MESSAGES</Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>No messages exchanged yet.</Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
      <Stack gap="md">
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>MESSAGES</Text>
        <Stack gap="sm">
          {flow.map((e) => {
            const isLeft = e.side === 'left';
            return (
              <Group
                key={`flow-${e.key}`}
                gap="sm"
                wrap="nowrap"
                align="flex-start"
                style={{ flexDirection: isLeft ? 'row' : 'row-reverse' }}
              >
                <Stack gap={4} align={isLeft ? 'flex-start' : 'flex-end'} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)', textAlign: isLeft ? 'left' : 'right' }}>
                    {formatDateTime(e.ts)}
                  </Text>
                  <Box
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      maxWidth: '85%',
                      ...(isLeft
                        ? {
                          alignSelf: 'flex-start',
                          border: '1px solid var(--mantine-color-gray-4)',
                          background: 'var(--mantine-color-gray-1)',
                        }
                        : {
                          alignSelf: 'flex-end',
                          border: '1px solid var(--mantine-color-sage-3)',
                          background: 'var(--mantine-color-sage-0)',
                        }),
                    }}
                  >
                    <Text
                      size="sm"
                      style={{
                        color: 'var(--aviary-ink)',
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {e.body}
                    </Text>
                  </Box>
                </Stack>
              </Group>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}

// AVI-27 Plan tab — shows the current Hermes strategy for this lead
// (adopted rules, rationale, playbook version) and, once the episode is
// closed, the Librarian retrospective (summary + applied-rules outcomes
// + candidate new rules + confidence). Non-editable for now — Jackie's
// read-only window into what the agent is doing and what it's learning.
function PlanCard({ plan }) {
  if (!plan || !plan.episode) {
    return (
      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>PLAN</Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            No agent context yet. A strategy appears once Hermes opens an episode on this lead.
          </Text>
        </Stack>
      </Paper>
    );
  }

  const { episode, strategy, retrospective } = plan;
  const closed = episode.status === 'closed';
  const rules = Array.isArray(strategy?.adoptedRules) ? strategy.adoptedRules : [];
  const exploring = Boolean(strategy?.exploring);

  return (
    <Stack gap="md">
      {/* Strategy card — always shown when we have an episode */}
      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Group justify="space-between" align="baseline">
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
              STRATEGY
            </Text>
            <Group gap={6} wrap="wrap">
              <Badge size="xs" variant="light" color="gray">
                playbook v{strategy?.playbookVersion ?? '?'}
              </Badge>
              {exploring && (
                <Badge size="xs" variant="light" color="yellow">EXPLORING</Badge>
              )}
              <Badge size="xs" variant="light" color={closed ? 'gray' : 'sage'}>
                {closed ? `closed · ${episode.closeReason || 'unknown'}` : episode.convoState || 'open'}
              </Badge>
              <Badge size="xs" variant="light" color="gray">
                budget {episode.budgetUsed}
              </Badge>
            </Group>
          </Group>

          {strategy?.rationale ? (
            <Box>
              <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                RATIONALE
              </Text>
              <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {strategy.rationale}
              </Text>
            </Box>
          ) : (
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>No rationale recorded yet.</Text>
          )}

          <Box>
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
              ADOPTED RULES ({rules.length})
            </Text>
            {rules.length ? (
              <Stack gap={4}>
                {rules.map((rule) => (
                  <Text
                    key={String(rule)}
                    size="xs"
                    style={{ color: 'var(--aviary-ink)', fontFamily: 'ui-monospace, monospace' }}
                  >
                    · {String(rule)}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>No rules adopted.</Text>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Retrospective — always rendered. Shows real data when the Librarian
          has produced it; otherwise falls back to a labeled sample preview so
          Jackie sees the full shape of what she'll get when a close completes.
          Preview is marked clearly so it can't be mistaken for a real retro. */}
      <RetrospectiveCard retrospective={retrospective} episodeClosed={closed} />
    </Stack>
  );
}

const SAMPLE_RETROSPECTIVE = {
  summary:
    'Prospect converted in 4 touches. Interest surfaced after the second send_message when the conversation shifted from "we are early" to "would love to know more". Hermes pivoted to send_booking_nudge on the next tick and the nudge-with-tracked-link converted on the first attempt.',
  appliedRulesOutcome: [
    {
      ruleId: 'seed.intro.welcome-no-pitch',
      outcome: 'support',
      justification: 'Intro landed a reply within 24h; no pitch kept the opener low-friction.',
    },
    {
      ruleId: 'seed.engage.only-after-reply',
      outcome: 'support',
      justification: 'All send_message decisions were in response to inbound replies; none pushed into silence.',
    },
    {
      ruleId: 'seed.convert.link-after-yes.v2',
      outcome: 'support',
      justification: 'Nudge fired on the first "love to know more" signal and converted on first attempt.',
    },
    {
      ruleId: 'seed.pacing.anti-stall',
      outcome: 'insufficient_evidence',
      justification: 'Rule did not need to fire — close came before the 3-send_message threshold.',
    },
  ],
  candidateRules: [
    {
      id: 'proposed.readiness-signal.learning-intent',
      statement: 'Treat "would love to know more about how to do it" as a hard close signal even without an explicit yes.',
      evidence: 'This episode converted on exactly that phrase; no explicit yes was given.',
      confidence: 0.7,
    },
    {
      id: 'proposed.length.mirror-short-replies',
      statement: 'When prospect reply is < 100 chars, keep send_message under 200 chars to avoid tone mismatch.',
      evidence: 'Last 3 outbound-to-reply pairs stayed within 200% char ratio and prospect kept engaging.',
      confidence: 0.55,
    },
  ],
  confidence: 0.78,
  llm: { provider: 'kimi', model: 'kimi-k2.6' },
  promptVersion: 'librarian.reflect.v1',
  completedAt: '2026-10-13T04:30:00.000Z',
};

function RetrospectiveCard({ retrospective, episodeClosed }) {
  const hasReal = Boolean(retrospective && retrospective.status !== 'pointer-only' && (retrospective.summary || retrospective.appliedRulesOutcome?.length));
  const data = hasReal ? retrospective : SAMPLE_RETROSPECTIVE;
  const isPreview = !hasReal;
  const previewReason = !episodeClosed
    ? 'Episode is still open — this is a sample of what the retrospective will look like once Hermes closes it.'
    : retrospective
      ? 'The Librarian stored a short summary pointer but the full job doc is not available yet.'
      : 'Reflection pending. The Librarian ticks every 5 min; this is a sample of what the retrospective will look like when it lands.';

  return (
    <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
      <Stack gap="md">
        {Boolean(data?.degraded) && (
          <Text size="xs" style={{ color: 'var(--mantine-color-yellow-8)', lineHeight: 1.5 }}>
            This retro ran in degraded mode (reason: {data.degradedReason || 'unknown'}). Expected v2 shape partially missing.
          </Text>
        )}

        <Group justify="space-between" align="baseline">
          <Group gap={8} align="baseline">
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
              RETROSPECTIVE
            </Text>
            {isPreview && (
              <Badge size="xs" variant="light" color="yellow">SAMPLE PREVIEW</Badge>
            )}
          </Group>
          {data.llm?.model && (
            <Group gap={6} wrap="wrap">
              <Badge size="xs" variant="light" color="gray">
                {data.llm.provider}/{data.llm.model}
              </Badge>
              {data.promptVersion && (
                <Badge size="xs" variant="light" color="gray">
                  {data.promptVersion}
                </Badge>
              )}
            </Group>
          )}
        </Group>

        {isPreview && (
          <Text size="xs" style={{ color: 'var(--aviary-muted)', fontStyle: 'italic', lineHeight: 1.5 }}>
            {previewReason}
          </Text>
        )}

        {retrospective?.status === 'pointer-only' ? (
          <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55 }}>
            {retrospective.summary}
          </Text>
        ) : (
          <>
            <Box>
              <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                SUMMARY
              </Text>
              <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                {data.summary || '—'}
              </Text>
            </Box>

            {Array.isArray(data.turnByTurn) && data.turnByTurn.length > 0 && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  TURN BY TURN
                </Text>
                <Table withTableBorder withColumnBorders horizontalSpacing="xs" verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>#</Table.Th>
                      <Table.Th>dir</Table.Th>
                      <Table.Th>sentiment</Table.Th>
                      <Table.Th>intent</Table.Th>
                      <Table.Th>agent error</Table.Th>
                      <Table.Th>note</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data.turnByTurn.map((turn, i) => (
                      <Table.Tr key={`tbt-${i}`}>
                        <Table.Td><Text size="xs">{turn.idx ?? i + 1}</Text></Table.Td>
                        <Table.Td><Text size="xs">{turn.direction || '—'}</Text></Table.Td>
                        <Table.Td>
                          <Badge size="xs" variant="light" color="gray">
                            {turn.sentiment || 'neutral'}
                          </Badge>
                        </Table.Td>
                        <Table.Td><Text size="xs">{turn.intent || 'other'}</Text></Table.Td>
                        <Table.Td>
                          {turn.agentError ? (
                            <Badge size="xs" variant="light" color="red">{turn.agentError}</Badge>
                          ) : (
                            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>—</Text>
                          )}
                        </Table.Td>
                        <Table.Td><Text size="xs">{turn.note || '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Box>
            )}

            {data.behavioralSignals && typeof data.behavioralSignals === 'object' && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  BEHAVIORAL SIGNALS
                </Text>
                <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.5 }}>
                  clicks {Number(data.behavioralSignals.clickCount || 0)} · first {data.behavioralSignals.firstClickAt || '—'} · last {data.behavioralSignals.lastClickAt || '—'}
                </Text>
                {data.behavioralSignals.divergentFromVerbal === true && (
                  <Text size="xs" style={{ color: 'var(--mantine-color-yellow-8)', marginTop: 4 }}>
                    Divergence detected: {data.behavioralSignals.divergenceNote || 'behavioral engagement diverged from verbal signal'}
                  </Text>
                )}
              </Box>
            )}

            {Array.isArray(data.pragmaticFailures) && data.pragmaticFailures.length > 0 && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  PRAGMATIC FAILURES
                </Text>
                <Stack gap={8}>
                  {data.pragmaticFailures.map((row, i) => (
                    <Box key={`pf-${i}`} style={{ borderLeft: '2px solid var(--mantine-color-yellow-4)', paddingLeft: 10 }}>
                      <Group gap={6} align="baseline">
                        <Badge
                          size="xs"
                          variant="light"
                          color={row.severity === 'high' ? 'red' : row.severity === 'medium' ? 'yellow' : 'gray'}
                        >
                          {row.severity || 'low'}
                        </Badge>
                        <Text size="xs" style={{ color: 'var(--aviary-ink)', fontFamily: 'ui-monospace, monospace' }}>
                          turn {row.turnIdx ?? '—'} · {row.type || 'unknown'}
                        </Text>
                      </Group>
                      <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.5, marginTop: 2 }}>
                        {row.description || '—'}
                      </Text>
                      {row.howToFix && (
                        <Text size="xs" style={{ color: 'var(--aviary-muted)', marginTop: 2, lineHeight: 1.5 }}>
                          How to fix: {row.howToFix}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {data.appliedRulesOutcome?.length > 0 && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  APPLIED RULES — DID THEY HOLD?
                </Text>
                <Stack gap={6}>
                  {data.appliedRulesOutcome.map((row, i) => (
                    <Box key={`rule-outcome-${i}`}>
                      <Group gap={6} wrap="nowrap" align="baseline">
                        <Badge
                          size="xs"
                          variant="light"
                          color={row.outcome === 'support' ? 'sage' : row.outcome === 'contradict' ? 'red' : 'gray'}
                        >
                          {row.outcome || 'unknown'}
                        </Badge>
                        <Text size="xs" style={{ color: 'var(--aviary-ink)', fontFamily: 'ui-monospace, monospace' }}>
                          {row.ruleId || row.id || '—'}
                        </Text>
                      </Group>
                      {row.justification && (
                        <Text size="xs" style={{ color: 'var(--aviary-muted)', marginLeft: 60, lineHeight: 1.5 }}>
                          {row.justification}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {data.candidateRules?.length > 0 && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  PROPOSED NEW RULES
                </Text>
                <Stack gap={8}>
                  {data.candidateRules.map((rule, i) => (
                    <Box key={`cand-${i}`} style={{ borderLeft: '2px solid var(--mantine-color-sage-3)', paddingLeft: 10 }}>
                      <Group gap={6} align="baseline">
                        <Text size="xs" style={{ color: 'var(--aviary-ink)', fontFamily: 'ui-monospace, monospace' }}>
                          {rule.id || `proposed-${i + 1}`}
                        </Text>
                        {typeof rule.confidence === 'number' && (
                          <Badge size="xs" variant="light" color="gray">conf {rule.confidence}</Badge>
                        )}
                      </Group>
                      {rule.statement && (
                        <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.5, marginTop: 2 }}>
                          {rule.statement}
                        </Text>
                      )}
                      {rule.evidence && (
                        <Text size="xs" style={{ color: 'var(--aviary-muted)', marginTop: 2, lineHeight: 1.5 }}>
                          evidence: {rule.evidence}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {data.outcomeCorrection && typeof data.outcomeCorrection === 'object' && (
              <Box>
                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.3, marginBottom: 4 }}>
                  OUTCOME CORRECTION
                </Text>
                <Text size="sm" style={{ color: 'var(--mantine-color-yellow-8)', lineHeight: 1.5 }}>
                  Suggested close reason: {data.outcomeCorrection.suggestedCloseReason || '—'} (confidence {data.outcomeCorrection.confidence ?? '—'}) — {data.outcomeCorrection.reasoning || 'No reasoning provided.'}
                </Text>
              </Box>
            )}

            {data.confidence != null && (
              <Group justify="flex-end">
                <Badge size="xs" variant="light" color={data.confidence >= 0.6 ? 'sage' : 'gray'}>
                  conf {data.confidence}
                </Badge>
              </Group>
            )}

            {retrospective?.error && (
              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                Retrospective errored: {retrospective.error.message}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

function TimelineCard({ entries }) {
  if (!entries.length) {
    return (
      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>TIMELINE</Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>No activity yet.</Text>
        </Stack>
      </Paper>
    );
  }
  return (
    <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
      <Stack gap="md">
        <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>TIMELINE</Text>
        <Stack gap="sm">
          {entries.map((e) => (
            <Group
              key={e.key}
              gap="sm"
              wrap="nowrap"
              align="flex-start"
              style={{ flexDirection: e.side === 'left' ? 'row' : 'row-reverse' }}
            >
              {(() => {
                const actorLabel = e.showActorBadge ? actorBadgeLabel(e.actorDisplay, e.actorSub) : '';
                const showSystemBadge = Boolean(e.systemBadge);
                // Chat-style justification: prospect activity sits left,
                // tenant/Aviary/system activity sits right. Default right.
                const isLeft = e.side === 'left';
                return (
                  <>
              <Box
                style={{
                  marginTop: 6,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  border: `2px solid ${e.optOut ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-sage-6)'}`,
                  background: e.filled
                    ? (e.optOut ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-sage-6)')
                    : 'transparent',
                  flexShrink: 0,
                }}
              />
              <Stack gap={4} align={isLeft ? 'flex-start' : 'flex-end'} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" style={{ color: 'var(--aviary-ink)', textAlign: isLeft ? 'left' : 'right' }}>
                  {e.title}
                  {e.articleTitle ? ' ' : ''}
                  {e.articleTitle ? (
                    e.articleHref ? (
                      <a
                        href={e.articleHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--mantine-color-sage-7)' }}
                      >
                        "{e.articleTitle}" ↗
                      </a>
                    ) : (
                      `"${e.articleTitle}"`
                    )
                  ) : null}
                </Text>
                <Group gap={6} wrap="wrap" align="center" justify={isLeft ? 'flex-start' : 'flex-end'}>
                  <Text size="xs" style={{ color: 'var(--aviary-muted)', textAlign: isLeft ? 'left' : 'right' }}>
                    {formatDateTime(e.ts)}{e.detail ? ` · ${e.detail}` : ''}
                  </Text>
                  {showSystemBadge && (
                    <Badge size="xs" variant="light" color="gray">
                      SYSTEM
                    </Badge>
                  )}
                  {actorLabel && (
                    <Badge size="xs" variant="light" color="gray">
                      {actorLabel}
                    </Badge>
                  )}
                </Group>
                {e.ua && (
                  <Text
                    size="xs"
                    style={{
                      color: 'var(--aviary-muted)',
                      lineHeight: 1.45,
                      wordBreak: 'break-word',
                    }}
                  >
                    {e.ua}
                  </Text>
                )}
                {e.body && (
                  <Box
                    style={{
                      marginTop: 4,
                      padding: '8px 12px',
                      borderRadius: 10,
                      maxWidth: '85%',
                      ...(isLeft
                        ? {
                          alignSelf: 'flex-start',
                          border: '1px solid var(--mantine-color-gray-4)',
                          background: 'var(--mantine-color-gray-1)',
                        }
                        : {
                          alignSelf: 'flex-end',
                          border: '1px solid var(--mantine-color-sage-3)',
                          background: 'var(--mantine-color-sage-0)',
                        }),
                    }}
                  >
                    <Text
                      size="xs"
                      style={{
                        color: 'var(--aviary-ink)',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {e.body}
                    </Text>
                  </Box>
                )}
              </Stack>
                  </>
                );
              })()}
            </Group>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

// ─── Article tab content ─────────────────────────────────────────────────────
// Renders the ONE actionable pending article draft (article-promo or
// article-share source) with editable body + Delete/Skip/Approve. Approve/Skip
// PATCH the whole articleDrafts array on the lead (draft entries gain
// approvedAt or skippedAt in place — history stays intact).
function ArticleDraftEditor({ draft, onApprove, onSkip, onDelete, busy, approveLabel = 'Approve →' }) {
  const [body, setBody] = useState(String(draft.messageBody || ''));
  useEffect(() => { setBody(String(draft.messageBody || '')); }, [draft.id, draft.messageBody]);
  return (
    <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Group gap="xs" align="baseline">
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
              MESSAGE · ARTICLE
            </Text>
            <Text size="xs" style={{ color: 'var(--mantine-color-sage-7)' }}>
              {draft.articleTitle || draft.articleSlug}
            </Text>
          </Group>
          <Badge size="xs" color="sage" variant="light">
            {draft.tone || 'warm'}
          </Badge>
        </Group>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          minRows={5}
          autosize
          disabled={busy}
          styles={{
            input: {
              background: 'var(--aviary-surface)',
              border: '1px solid var(--aviary-disabled)',
              borderRadius: 10,
              padding: 14,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: 'var(--aviary-ink)',
            },
          }}
        />
        <Group gap="sm" justify="space-between" wrap="nowrap">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onDelete}
            disabled={busy}
          >
            Delete
          </Button>
          <Group gap="sm" wrap="nowrap">
            <Button variant="default" color="gray" onClick={() => onSkip(draft)} disabled={busy}>
              Skip
            </Button>
            <Button
              color="sage"
              onClick={() => onApprove(draft, body)}
              disabled={busy || !body.trim()}
            >
              {approveLabel}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}

// LinkedIn caps invitation notes at 300 characters. Enforced client-side
// because LinkedIn silently truncates or rejects longer notes.
const LINKEDIN_INVITE_LIMIT = 300;
const SEGMENTED_TABS_STYLES = {
  list: {
    border: '1px solid var(--aviary-disabled)',
    borderRadius: 12,
    padding: 4,
    gap: 4,
    background: '#fff',
    display: 'inline-flex',
    width: 'fit-content',
  },
  // Active tab now has explicit white-on-sage styling at the base level
  // (was previously only set via per-tab inline style, which left the
  // base muted colour showing through on some tabs and caused the hard-to-
  // read dim text on sage background). Hover state darkens one step so the
  // active tab visually responds to pointer-over.
  tab: {
    borderRadius: 8,
    color: 'var(--aviary-ink)',
    fontWeight: 600,
    paddingLeft: 14,
    paddingRight: 14,
    '&:not([data-active]):hover': {
      background: 'var(--aviary-surface)',
    },
    '&[data-active]': {
      background: 'var(--mantine-color-sage-6)',
      color: '#fff',
    },
    '&[data-active]:hover': {
      background: 'var(--mantine-color-sage-7)',
      color: '#fff',
    },
  },
};

function MessageEditor({
  messageDraft,
  onMessageChange,
  onDelete,
  onSkip,
  onApprove,
  onSendInvite,
  approving,
  skipping,
  sendingInvite,
  touches,
  channelLabel,
  textareaRef,
  noCustomOpenerMode,
  linkedinUrl,
}) {
  const followUps = Math.max(0, (touches?.length || 0) - 1);
  const isLinkedinInvite = String(channelLabel || '').toLowerCase().includes('linkedin');
  const charCount = messageDraft.length;
  const overLimit = isLinkedinInvite && charCount > LINKEDIN_INVITE_LIMIT;
  const linkedinHref = String(linkedinUrl || '').trim();

  if (noCustomOpenerMode) {
    return (
      <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
        <Stack gap="md">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            MESSAGE · NOTELESS INVITE
          </Text>
          <Paper
            p="sm"
            radius="md"
            style={{
              background: 'var(--mantine-color-sage-0)',
              border: '1px solid var(--mantine-color-sage-3)',
            }}
          >
            <Stack gap={6}>
              <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-sage-8)' }}>
                Noteless invite
              </Text>
              <Text size="sm" style={{ color: 'var(--aviary-ink)' }}>
                Send LinkedIn&apos;s default Connect invite — no message attached. Saves your custom-note quota.
              </Text>
              {linkedinHref && (
                <UnstyledButton
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(linkedinHref);
                      notifications.show({ title: 'LinkedIn URL copied', message: 'Paste it into your browser if needed.', color: 'sage' });
                    } catch (_err) {
                      notifications.show({ title: 'Copy failed', message: linkedinHref, color: 'yellow' });
                    }
                  }}
                  style={{
                    alignSelf: 'flex-start',
                    border: '1px dashed var(--mantine-color-sage-5)',
                    borderRadius: 999,
                    padding: '4px 10px',
                    color: 'var(--mantine-color-sage-8)',
                  }}
                >
                  <Text size="xs" fw={600}>Copy LinkedIn URL</Text>
                </UnstyledButton>
              )}
            </Stack>
          </Paper>
          <Group gap="sm" justify="space-between" wrap="nowrap">
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={onDelete}
              disabled={approving || skipping || sendingInvite}
            >
              Delete
            </Button>
            <Group gap="sm" wrap="nowrap">
              <Button variant="default" color="gray" onClick={onSkip} disabled={approving || skipping || sendingInvite} loading={skipping}>
                Skip
              </Button>
              <Button color="sage" onClick={onSendInvite} loading={sendingInvite} disabled={approving || skipping || sendingInvite}>
                Send invite →
              </Button>
            </Group>
          </Group>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            MESSAGE · EDITABLE
          </Text>
          <Tooltip label="Coming soon — will let you re-run the LLM with different tone" withArrow>
            <Group gap={4} align="center" style={{ cursor: 'not-allowed', opacity: 0.6 }}>
              <Text size="xs" c="sage" fw={600}>rewrite with AI</Text>
              <IconRefresh size={12} style={{ color: 'var(--mantine-color-sage-6)' }} />
            </Group>
          </Tooltip>
        </Group>
        <Textarea
          ref={textareaRef}
          value={messageDraft}
          onChange={(e) => onMessageChange(e.currentTarget.value)}
          autosize
          minRows={5}
          placeholder="Message body — edit freely before approving."
          styles={{
            input: {
              background: 'var(--aviary-surface)',
              border: `1px solid ${overLimit ? 'var(--mantine-color-red-6)' : 'var(--aviary-disabled)'}`,
              borderRadius: 10,
              padding: 14,
              fontSize: 14.5,
              lineHeight: 1.55,
              color: 'var(--aviary-ink)',
            },
          }}
        />
        <Group justify="space-between" align="baseline">
          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
            sends: next business hour · {channelLabel || 'preferred channel'}
            {followUps > 0 && ` · then ${followUps} follow-up${followUps === 1 ? '' : 's'} if quiet`}
          </Text>
          {isLinkedinInvite && (
            <Text
              size="xs"
              fw={overLimit ? 600 : 400}
              style={{ color: overLimit ? 'var(--mantine-color-red-6)' : 'var(--aviary-muted)' }}
            >
              {charCount}/{LINKEDIN_INVITE_LIMIT}
              {overLimit && ' — trim to send'}
            </Text>
          )}
        </Group>
        <Group gap="sm" justify="space-between" wrap="nowrap">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={onDelete}
            disabled={approving || skipping}
          >
            Delete
          </Button>
          <Group gap="sm" wrap="nowrap">
            <Button variant="default" color="gray" onClick={onSkip} disabled={approving || skipping} loading={skipping}>
              Skip
            </Button>
            <Button color="sage" onClick={onApprove} loading={approving} disabled={approving || skipping || !messageDraft.trim() || overLimit}>
              Invite →
            </Button>
          </Group>
        </Group>
      </Stack>
    </Paper>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────
export default function AviaryV2LeadDetailPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: leadId } = useParams();
  const queueParam = String(searchParams.get('queue') || '').toLowerCase();
  const queueCampaignParam = String(searchParams.get('campaign') || 'all');
  const queueMode = queueParam === 'connected';

  const [lead, setLead] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [touches, setTouches] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [siblingLeads, setSiblingLeads] = useState([]);
  const [principalName, setPrincipalName] = useState('');
  const [principalModes, setPrincipalModes] = useState(() => ({ ...CHANNEL_MODE_DEFAULTS }));
  const [connectedLaneMode, setConnectedLaneMode] = useState('manual');
  const [events, setEvents] = useState([]);
  const [noteActivities, setNoteActivities] = useState([]);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const [newNoteBody, setNewNoteBody] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [approving, setApproving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [markingAsSent, setMarkingAsSent] = useState(false);
  const [movingToBooked, setMovingToBooked] = useState(false);
  const [articleSendSlug, setArticleSendSlug] = useState('');
  const didConnectedPrepPing = useRef(false);
  const textareaRef = useRef(null);
  const articleDraftRef = useRef(null);

  // ─── Load ──
  const load = useCallback(async () => {
    try {
      const leadData = await fetchLead(api, leadId);
      setLead(leadData);
      setNewNoteBody('');

      const cid = leadData?.campaignId;
      const siblingsPromise = (() => {
        if (queueMode) {
          if (queueCampaignParam === 'all') return fetchLeads(api).catch(() => []);
          return fetchLeads(api, { campaignId: queueCampaignParam }).catch(() => []);
        }
        if (!cid) return Promise.resolve([]);
        return fetchLeads(api, { campaignId: cid }).catch(() => []);
      })();
      const [detail, siblings, principal, evData, actData, taskData, articleData, planData] = await Promise.all([
        cid ? fetchCampaign(api, cid).catch(() => null) : Promise.resolve(null),
        siblingsPromise,
        fetchMyPrincipal(api).catch(() => null),
        fetchLeadEvents(api, leadId).catch(() => ({ events: [] })),
        fetchActivities(api, leadId).catch(() => ({ activities: [] })),
        fetchLeadTasks(api, leadId).catch(() => ({ tasks: [] })),
        fetchArticles(api).catch(() => []),
        fetchLeadPlan(api, leadId).catch(() => null),
      ]);
      setCampaign(detail?.campaign || null);
      setTouches(Array.isArray(detail?.touches) ? detail.touches : []);
      const rawSiblings = Array.isArray(siblings) ? siblings : (siblings?.leads || []);
      // Archived leads are permanently out — keep only non-archived siblings, plus the
      // currently-viewed lead even if archived (so the page renders instead of 404-ing).
      setSiblingLeads(rawSiblings.filter((l) => l.archived !== true || String(l.id) === String(leadId)));
      setPrincipalName(String(principal?.displayName || principal?.name || '').trim());
      setPrincipalModes(mergedChannelModes(principal?.channelModes));
      setConnectedLaneMode(normalizeConnectedLaneMode(principal?.laneAutomation));
      const evList = Array.isArray(evData?.events) ? evData.events : (Array.isArray(evData) ? evData : []);
      setEvents(evList);
      const actList = Array.isArray(actData?.activities) ? actData.activities : (Array.isArray(actData) ? actData : []);
      setNoteActivities(actList);
      const taskList = Array.isArray(taskData?.tasks) ? taskData.tasks : (Array.isArray(taskData) ? taskData : []);
      setTasks(taskList);
      const articleList = Array.isArray(articleData) ? articleData : [];
      setArticles(articleList);
      setPlan(planData || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, [api, leadId, queueMode, queueCampaignParam]);
  useEffect(() => { load(); }, [load]);

  // ─── Derived state (all hooks BEFORE any early return) ──
  const back = useMemo(() => {
    if (location.state?.back?.path) return location.state.back;
    if (campaign?.id) return { path: `/v2/campaigns/${campaign.id}`, label: campaign.name || 'Campaign' };
    return { path: '/v2/campaigns', label: 'Campaigns' };
  }, [location.state, campaign]);

  const channelLabel = useMemo(() => channelLabelOf(campaign?.channel), [campaign]);

  const computedMessage = useMemo(() => {
    if (!lead) return '';
    const subOpts = {
      firstName: firstNameOf(lead.name) || 'there',
      personalisedIntro: lead.personalizedOpener || '',
      principalName,
      link: touches[0]?.destinationUrl || '',
    };
    if (lead.approvedMessage) return substituteTokens(lead.approvedMessage, subOpts);
    const template = String(touches[0]?.messageTemplate || '').trim();
    if (!template) return '';
    return substituteTokens(template, subOpts);
  }, [lead, touches, principalName]);

  // Reseed the message draft when we change lead (Skip/Approve → next).
  useEffect(() => {
    setMessageDraft(computedMessage);
  }, [computedMessage, leadId]);

  const timeline = useMemo(
    () => buildSynthesizedTimeline({ lead, campaign, events, channelLabel, noteActivities }),
    [lead, campaign, events, channelLabel, noteActivities]
  );

  // Article drafts pending on this lead (awaiting coach approval).
  const pendingArticleDrafts = useMemo(() => {
    if (!lead || !Array.isArray(lead.articleDrafts)) return [];
    return lead.articleDrafts.filter((d) => !isIntroMessageDraft(d) && isPendingArticleDraft(d));
  }, [lead]);
  // The ONE actionable draft shown in the Article tab: oldest pending,
  // preferring article-promo over article-share default previews.
  const activeArticleDraft = useMemo(() => {
    if (!pendingArticleDrafts.length) return null;
    const sorted = [...pendingArticleDrafts].sort((a, b) => articleDraftQueueTs(a) - articleDraftQueueTs(b));
    return sorted.find((d) => String(d?.source || '').toLowerCase() === 'article-promo') || sorted[0];
  }, [pendingArticleDrafts]);
  const approvedUnsentArticleDraft = useMemo(() => {
    if (!lead?.articleDrafts) return null;
    return lead.articleDrafts.find(
      (d) => !isIntroMessageDraft(d) && d.approvedAt && !d.sentAt && !d.skippedAt
    ) || null;
  }, [lead]);
  const flowLabel = String(lead?.flow || campaign?.flow || '').toLowerCase();
  const isCoachFlow = flowLabel !== 'business';
  const publishedArticles = useMemo(() => {
    const list = Array.isArray(articles) ? articles : [];
    return list
      .filter((article) => String(article?.articleStatus || '').toLowerCase() === 'published')
      .sort((a, b) => (
        new Date(b?.updatedAt || b?.createdAt || 0).getTime()
        - new Date(a?.updatedAt || a?.createdAt || 0).getTime()
      ));
  }, [articles]);
  const articleTableRows = useMemo(() => {
    const magicLinks = Array.isArray(lead?.magicLinks) ? lead.magicLinks : [];
    const drafts = Array.isArray(lead?.articleDrafts) ? lead.articleDrafts : [];
    const rows = publishedArticles.map((article) => {
      const slug = String(article?.slug || '').trim();
      const matching = magicLinks.filter((item) => {
        const itemSlug = String(item?.articleSlug || '').trim()
          || inferArticleSlugFromUrl(item?.destinationUrl, lead?.principalId);
        return slug && itemSlug === slug;
      });
      const sentMatches = matching.filter((item) => item?.sentAt);
      const queuedMatches = matching.filter((item) => !item?.sentAt);
      // Also consult articleDrafts[] — v2PipelineShare.js sometimes fails to
      // add a magic link when a draft is created (race, task #25), so an
      // approved-but-not-sent article-share draft can exist with no matching
      // magicLink. Without this, the AVAILABLE ARTICLES row would show
      // "Available" + a Send button even though the lead already has a queued
      // draft for it — clicking Send would create a duplicate.
      const draftMatches = drafts.filter((d) => {
        if (String(d?.source || '') !== 'article-share') return false;
        if (String(d?.status || '').toLowerCase() === 'skipped') return false;
        return String(d?.articleSlug || '') === slug;
      });
      const draftQueuedMatches = draftMatches.filter((d) => !d?.sentAt && !d?.failedAt);
      const draftSentMatches = draftMatches.filter((d) => Boolean(d?.sentAt));
      const opens = sentMatches.filter((item) => Number(item?.clickCount || 0) > 0).length;
      const reads = sentMatches.filter((item) => item?.readAt).length;
      const latestSentAt = [
        ...sentMatches.map((item) => item?.sentAt),
        ...draftSentMatches.map((d) => d?.sentAt),
      ]
        .filter(Boolean)
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;
      const isQueued = queuedMatches.length > 0 || draftQueuedMatches.length > 0;
      const isSent = sentMatches.length > 0 || draftSentMatches.length > 0;
      const status = isQueued ? 'queued' : (isSent ? 'sent' : 'available');
      return {
        article,
        status,
        opens,
        reads,
        sentAt: latestSentAt,
      };
    });
    return rows.sort((a, b) => {
      const rank = (row) => (row.status === 'sent' ? 1 : 0);
      const rankDiff = rank(a) - rank(b);
      if (rankDiff !== 0) return rankDiff;
      return new Date(b.article?.updatedAt || b.article?.createdAt || 0).getTime()
        - new Date(a.article?.updatedAt || a.article?.createdAt || 0).getTime();
    });
  }, [lead?.magicLinks, lead?.articleDrafts, lead?.principalId, publishedArticles]);
  const isManualLinkedin = Number(principalModes?.linkedin ?? 0) === 0;
  const [articleDraftBusy, setArticleDraftBusy] = useState(false);
  const [introDraftBusy, setIntroDraftBusy] = useState(false);
  const [introDraftBody, setIntroDraftBody] = useState('');

  const coachingNotes = useMemo(() => {
    if (!lead) return [];
    const list = Array.isArray(lead.coachingNotes) ? lead.coachingNotes : [];
    return [...list].sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
  }, [lead]);

  const reloadTasks = useCallback(async () => {
    const taskData = await fetchLeadTasks(api, leadId);
    const taskList = Array.isArray(taskData?.tasks) ? taskData.tasks : [];
    setTasks(taskList);
    return taskList;
  }, [api, leadId]);

  // ─── Needs-approval + tab resolution ──
  const needsApproval = Boolean(
    lead
    && String(lead.stage || 'prospect').toLowerCase() === 'prospect'
    && !lead.approvedAt
    && !lead.archived
  );
  const introState = useMemo(() => getIntroProposalState(lead), [lead]);
  const {
    isConnected,
    latestIntroDraft,
    introSkipped,
    introTabVisible,
    articleTabVisible,
    shouldProposeIntro,
  } = introState;
  const pendingCount = (needsApproval ? 1 : 0) + pendingArticleDrafts.length + (shouldProposeIntro ? 1 : 0);
  const queuedManualTask = useMemo(() => {
    return (tasks || [])
      .filter((task) => String(task.status || '').toLowerCase() === 'queued')
      .sort((a, b) => new Date(a.dueAt || 0).getTime() - new Date(b.dueAt || 0).getTime())
      .find((task) => {
        const channel = String(task.channel || '').toLowerCase();
        return Number(principalModes[channel]) === 0;
      }) || null;
  }, [tasks, principalModes]);
  const paramTab = searchParams.get('tab');
  const activeTab = useMemo(() => {
    if (isConnected) {
      const allowed = introTabVisible
        ? ['message', 'intro', 'article', 'activity', 'plan']
        : ['message', 'article', 'activity', 'plan'];
      if (allowed.includes(String(paramTab || ''))) return paramTab;
      if (introTabVisible) return introSkipped ? 'article' : 'intro';
      return 'article';
    }
    if (paramTab === 'message') return 'message';
    if (paramTab === 'activity') return 'activity';
    if (paramTab === 'plan') return 'plan';
    return pendingCount > 0 ? 'message' : 'activity';
  }, [paramTab, pendingCount, isConnected, introTabVisible, introSkipped]);

  function handleTabChange(next) {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  // ─── Next-in-queue for silent Skip/Approve navigation ──
  const nextUnapproved = useMemo(() => {
    const currentIdx = siblingLeads.findIndex((l) => String(l.id) === String(leadId));
    if (currentIdx < 0) return null;
    return siblingLeads
      .slice(currentIdx + 1)
      .find((l) => String(l.stage || 'prospect').toLowerCase() === 'prospect' && !l.approvedAt && !l.archived) || null;
  }, [siblingLeads, leadId]);

  const connectedQueue = useMemo(() => {
    if (!queueMode) return [];
    return (siblingLeads || [])
      .filter((item) => item.archived !== true)
      .filter((item) => isConnectedQueueStage(item.stage))
      .filter((item) => !hasSentArticleShareDraft(item))
      .sort((a, b) => new Date(b.lastTouchAt || b.updatedAt || b.createdAt || 0).getTime()
        - new Date(a.lastTouchAt || a.updatedAt || a.createdAt || 0).getTime());
  }, [queueMode, siblingLeads]);

  const queueIndex = useMemo(
    () => connectedQueue.findIndex((item) => String(item.id) === String(leadId)),
    [connectedQueue, leadId]
  );
  const queueCount = connectedQueue.length;
  const prevQueuedLead = queueIndex > 0 ? connectedQueue[queueIndex - 1] : null;
  const nextQueuedLead = queueIndex >= 0 && queueIndex < queueCount - 1 ? connectedQueue[queueIndex + 1] : null;

  const queueLabel = useMemo(() => {
    if (queueCampaignParam === 'all') return campaign?.name || 'All campaigns';
    if (campaign?.id && String(campaign.id) === String(queueCampaignParam)) return campaign.name || 'Campaign';
    const byFilter = siblingLeads.find((item) => String(item.campaignId || '') === String(queueCampaignParam));
    return String(byFilter?.campaignName || '').trim() || campaign?.name || 'Campaign';
  }, [queueCampaignParam, campaign, siblingLeads]);

  const nextPendingArticleDraftLead = useMemo(() => {
    const candidates = siblingLeads
      .filter((l) => String(l.id) !== String(leadId))
      .map((candidateLead) => {
        const pendingDrafts = (candidateLead.articleDrafts || []).filter(isPendingArticleDraft);
        if (!pendingDrafts.length) return null;
        const earliestTs = Math.min(...pendingDrafts.map(articleDraftQueueTs));
        return { lead: candidateLead, earliestTs };
      })
      .filter(Boolean)
      .sort((a, b) => a.earliestTs - b.earliestTs);
    return candidates[0]?.lead || null;
  }, [siblingLeads, leadId]);

  useEffect(() => {
    if (didConnectedPrepPing.current) return;
    if (connectedLaneMode !== 'semi') return;
    didConnectedPrepPing.current = true;
    pingConnectedAutoNextTask(api).catch(() => {});
  }, [api, connectedLaneMode]);

  useEffect(() => {
    if (!introTabVisible) {
      setIntroDraftBody('');
      return;
    }

    if (latestIntroDraft?.messageBody) {
      setIntroDraftBody(String(latestIntroDraft.messageBody || ''));
    }
    if (activeTab !== 'intro') return;
    if (latestIntroDraft?.messageBody) return;

    let cancelled = false;
    setIntroDraftBusy(true);
    generateIntroDraft(api, leadId)
      .then((payload) => {
        if (cancelled) return;
        const introDraft = payload?.draft || null;
        if (!introDraft) return;
        setLead((prev) => mergeDraftIntoLead(prev, introDraft));
        setIntroDraftBody(String(introDraft.messageBody || ''));
      })
      .catch((err) => {
        if (cancelled) return;
        notifications.show({
          title: 'Could not generate intro',
          message: humanizeDraftGuardError(err),
          color: 'red',
        });
      })
      .finally(() => {
        if (!cancelled) setIntroDraftBusy(false);
      });

    return () => { cancelled = true; };
  }, [api, leadId, activeTab, introTabVisible, latestIntroDraft?.id, latestIntroDraft?.messageBody]);

  // ─── Handlers ──
  function goBack() {
    if (queueMode) {
      navigate('/v2/board');
      return;
    }
    navigate(back.path);
  }

  function goToQueuedLead(nextLead) {
    if (!nextLead?.id) {
      navigate('/v2/board');
      return;
    }
    const params = new URLSearchParams();
    params.set('queue', 'connected');
    params.set('campaign', queueCampaignParam || 'all');
    navigate(`/v2/leads/${nextLead.id}?${params.toString()}`);
  }

  function navigateAfterQueueAction() {
    if (!queueMode) return false;
    if (nextQueuedLead) {
      goToQueuedLead(nextQueuedLead);
    } else {
      navigate('/v2/board');
    }
    return true;
  }

  async function handleMoveToBooked() {
    if (!lead) return;
    setMovingToBooked(true);
    try {
      const nowIso = new Date().toISOString();
      const updated = await updateLead(api, lead.id, { stage: 'booked', bookedAt: nowIso });
      setLead((prev) => ({ ...prev, ...updated, stage: 'booked', bookedAt: nowIso }));
      notifications.show({ title: 'Booked', message: `${lead.name || 'Prospect'} moved to Booked.`, color: 'sage' });
    } catch (err) {
      notifications.show({ title: 'Could not update stage', message: err.response?.data?.error || err.message, color: 'red' });
    } finally {
      setMovingToBooked(false);
    }
  }

  async function handleSaveNotes() {
    if (!lead) return;
    const trimmed = newNoteBody.trim();
    if (!trimmed) return;
    setNotesSaving(true);
    try {
      const updatedLead = await updateLead(api, lead.id, {
        coachingNotes: { add: { body: trimmed } },
      });
      setLead(updatedLead);
      const nextActivities = await fetchActivities(api, lead.id).catch(() => null);
      const noteList = Array.isArray(nextActivities?.activities)
        ? nextActivities.activities
        : (Array.isArray(nextActivities) ? nextActivities : null);
      if (noteList) setNoteActivities(noteList);
      setNewNoteBody('');
      notifications.show({ title: 'Note saved', message: 'Added to the timeline.', color: 'sage' });
    } catch (err) {
      notifications.show({ title: 'Could not save note', message: err.response?.data?.error || err.message, color: 'red' });
    } finally {
      setNotesSaving(false);
    }
  }

  function focusMessageTextarea() {
    if (textareaRef.current) {
      textareaRef.current.focus();
      const len = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(len, len);
    }
  }

  async function handleSkipMessage() {
    if (!lead) return;
    if (queueMode && navigateAfterQueueAction()) return;
    setSkipping(true);
    try {
      await updateLead(api, lead.id, { archived: true, archivedBy: 'user' });
    } catch (err) {
      notifications.show({ title: 'Could not skip', message: err.response?.data?.error || err.message, color: 'red' });
      setSkipping(false);
      return;
    }
    setSkipping(false);
    if (nextUnapproved) {
      navigate(`/v2/leads/${nextUnapproved.id}?tab=message`, { state: location.state });
    } else {
      goBack();
    }
  }

  async function handleDeleteLead() {
    if (!lead) return;
    const confirmed = window.confirm(`Delete ${lead.name || 'this lead'}? This can't be undone.`);
    if (!confirmed) return;
    setSkipping(true);
    try {
      await updateLead(api, lead.id, { archived: true, archivedBy: 'user' });
      notifications.show({
        title: 'Deleted',
        message: `${lead.name || 'Lead'} was removed from your queue.`,
        color: 'gray',
      });
      if (!navigateAfterQueueAction()) navigate('/v2/board');
    } catch (err) {
      notifications.show({
        title: 'Could not delete lead',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setSkipping(false);
    }
  }

  async function handleApproveMessage() {
    if (!lead) return;

    // Synchronous browser actions FIRST — window.open + clipboard must fire in
    // the user-gesture context. Awaiting anything before them lets the browser
    // reject them as popup-blocked / permission-denied on subsequent clicks.
    const messageBody = messageDraft.trim() || String(lead.approvedMessage || '').trim();
    const linkedinUrl = String(lead?.linkedinUrl || lead?.socials?.linkedin || '').trim();
    if (linkedinUrl) {
      window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    }
    if (messageBody && navigator.clipboard) {
      // Fire-and-forget: don't await (would break the gesture chain for window.open above).
      navigator.clipboard.writeText(messageBody).catch(() => {});
    }

    setApproving(true);
    try {
      const nowIso = new Date().toISOString();
      await updateLead(api, lead.id, {
        stage: 'invited',
        approvedMessage: messageBody,
        approvedAt: nowIso,
        invitedAt: nowIso,
        lastTouchAt: nowIso,
      });
      notifications.show({
        title: 'Invite sent',
        message: nextUnapproved
          ? `Moved to Invited. Next: ${nextUnapproved.name || 'prospect'}…`
          : 'Moved to Invited. Queue is clear.',
        color: 'sage',
      });
      if (nextUnapproved) {
        navigate(`/v2/leads/${nextUnapproved.id}?tab=message`, { state: location.state });
      } else {
        goBack();
      }
    } catch (err) {
      notifications.show({ title: 'Could not send invite', message: err.response?.data?.error || err.message, color: 'red' });
    } finally {
      setApproving(false);
    }
  }

  async function handleMarkAsSent({ messageBodyOverride, copyToClipboard = true, successTitle = 'Sent via LinkedIn' } = {}) {
    if (!queuedManualTask) {
      notifications.show({
        title: 'No queued task found',
        message: 'Refresh and try again once the task appears.',
        color: 'yellow',
      });
      return;
    }

    const messageBody = messageBodyOverride !== undefined
      ? String(messageBodyOverride)
      : (queuedManualTask.messageBody || messageDraft || '');
    const linkedinUrl = String(lead?.linkedinUrl || lead?.socials?.linkedin || '').trim();

    // Browser actions first — must run inside the user-gesture context.
    if (copyToClipboard && messageBody) {
      try {
        await navigator.clipboard.writeText(messageBody);
      } catch (_err) {
        // Clipboard write can fail on permissions/focus — user can still paste after opening the tab.
      }
    }
    if (linkedinUrl) {
      window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    }

    setMarkingAsSent(true);
    try {
      await patchTask(api, queuedManualTask.id, { status: 'sent' }, queuedManualTask._etag);
      notifications.show({
        title: successTitle,
        message: nextUnapproved
          ? `${copyToClipboard ? 'Message copied.' : 'Invite marked sent.'} Moving to ${nextUnapproved.name || 'the next prospect'}…`
          : `${copyToClipboard ? 'Message copied.' : 'Invite marked sent.'} Queue is clear.`,
        color: 'sage',
      });
      if (nextUnapproved) {
        navigate(`/v2/leads/${nextUnapproved.id}?tab=message`, { state: location.state });
      } else {
        goBack();
      }
    } catch (err) {
      if (err?.response?.status === 412) {
        notifications.show({
          title: 'Already sent',
          message: 'This task was already sent by the system.',
          color: 'yellow',
        });
        await reloadTasks();
      } else {
        notifications.show({
          title: 'Could not mark as sent',
          message: err.response?.data?.error || err.message,
          color: 'red',
        });
      }
    } finally {
      setMarkingAsSent(false);
    }
  }

  // Approve/Skip one article-promo draft. Does NOT touch lead.approvedMessage
  // or stage — the campaign opener remains the primary approval flow.
  async function patchArticleDraft(draftId, patch) {
    if (!lead) return;
    setArticleDraftBusy(true);
    try {
      const nextDrafts = (lead.articleDrafts || []).map((d) => (
        d.id === draftId ? { ...d, ...patch } : d
      ));
      const updated = await updateLead(api, lead.id, { articleDrafts: nextDrafts });
      setLead(updated);
    } catch (err) {
      notifications.show({
        title: 'Could not update draft',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setArticleDraftBusy(false);
    }
  }
  async function handleApproveArticleDraft(draft, body) {
    if (connectedLaneMode === 'semi') {
      const now = new Date().toISOString();
      await patchArticleDraft(draft.id, {
        status: 'approved',
        approvedAt: now,
        messageBody: body.trim(),
      });
      notifications.show({
        title: 'Approved',
        message: `"${draft.articleTitle || draft.articleSlug}" is approved for extension send.`,
        color: 'sage',
      });
      if (navigateAfterQueueAction()) return;
      if (nextPendingArticleDraftLead) {
        navigate(`/v2/leads/${nextPendingArticleDraftLead.id}?tab=message`, { state: location.state });
      } else {
        navigate('/v2/board');
      }
      return;
    }

    await patchArticleDraft(draft.id, {
      approvedAt: new Date().toISOString(),
      messageBody: body.trim(),
    });
    notifications.show({
      title: 'Article promo approved',
      message: `"${draft.articleTitle || draft.articleSlug}" queued to send.`,
      color: 'sage',
    });
  }
  // Reset a specific draft back to queued state so the extension picks it up
  // on its next poll. Used for retrying a failed send OR nudging a stuck-claimed
  // draft. Preserves the original approvedAt so FIFO order is untouched.
  async function handleRequeueArticleDraft(draft) {
    if (!draft) return;
    await patchArticleDraft(draft.id, {
      status: 'approved',
      approvedAt: draft.approvedAt || new Date().toISOString(),
      failedAt: null,
      failureReason: null,
      claimedByExtension: null,
      claimedAt: null,
      dryRunAt: null,
    });
    notifications.show({
      title: 'Approved for retry',
      message: 'Extension will pick this up on its next poll (~30s).',
      color: 'sage',
    });
  }

  async function handleMarkArticleDraftSent(draft) {
    if (!draft) return;
    const body = String(draft.messageBody || '').trim();
    const linkedinUrl = String(lead?.linkedinUrl || lead?.socials?.linkedin || '').trim();
    if (body) {
      try { await navigator.clipboard.writeText(body); } catch (_err) { /* clipboard blocked; user can still paste after opening */ }
    }
    if (linkedinUrl) {
      window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    }
    setArticleDraftBusy(true);
    try {
      await patchArticleDraft(draft.id, {
        sentAt: new Date().toISOString(),
        status: 'sent',
      });
      notifications.show({
        title: 'Sent via LinkedIn',
        message: 'Message copied — paste and send in the LinkedIn tab.',
        color: 'sage',
      });
    } catch (err) {
      notifications.show({
        title: 'Could not mark sent',
        message: err?.response?.data?.error || err.message || 'Try again.',
        color: 'red',
      });
    } finally {
      setArticleDraftBusy(false);
    }
  }
  async function handleSkipArticleDraft(draft) {
    await patchArticleDraft(draft.id, { skippedAt: new Date().toISOString() });
    notifications.show({
      title: 'Skipped',
      message: `"${draft.articleTitle || draft.articleSlug}" won't send to this person.`,
      color: 'gray',
    });
  }

  function handleOpenQueuedDraft() {
    const card = articleDraftRef.current;
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const input = card.querySelector('textarea');
    if (input && typeof input.focus === 'function') input.focus();
  }

  async function handleShareArticle(article) {
    if (!lead?.id || !article?.slug) return;
    const slug = String(article.slug);
    setArticleSendSlug(slug);
    try {
      await shareArticleWithLead(api, lead.id, slug);
      await load();
      notifications.show({
        title: 'Draft generated',
        message: `Queued "${article.title || article.slug}" for review.`,
        color: 'sage',
      });
      setTimeout(() => handleOpenQueuedDraft(), 50);
    } catch (err) {
      notifications.show({
        title: 'Could not queue article',
        message: humanizeDraftGuardError(err),
        color: 'red',
      });
    } finally {
      setArticleSendSlug('');
    }
  }

  async function handleRewriteIntro() {
    if (!lead) return;
    setIntroDraftBusy(true);
    try {
      const payload = await generateIntroDraft(api, lead.id, { rewrite: true });
      const introDraft = payload?.draft || null;
      if (!introDraft) return;
      setLead((prev) => mergeDraftIntoLead(prev, introDraft));
      setIntroDraftBody(String(introDraft.messageBody || ''));
    } catch (err) {
      notifications.show({
        title: 'Could not rewrite intro',
        message: humanizeDraftGuardError(err),
        color: 'red',
      });
    } finally {
      setIntroDraftBusy(false);
    }
  }

  async function handleSkipIntroDraft() {
    if (!latestIntroDraft?.id) return;
    const now = new Date().toISOString();
    await patchArticleDraft(latestIntroDraft.id, { skippedAt: now, status: 'skipped' });
    if (queueMode && navigateAfterQueueAction()) return;
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'article');
    setSearchParams(params, { replace: true });
    notifications.show({
      title: 'Intro skipped',
      message: 'You can still revisit Intro and regenerate anytime.',
      color: 'gray',
    });
  }

  async function handleApproveIntroDraft() {
    if (!latestIntroDraft?.id) return;
    const body = String(introDraftBody || '').trim();
    if (!body) return;
    const now = new Date().toISOString();
    await patchArticleDraft(latestIntroDraft.id, {
      messageBody: body,
      approvedAt: now,
      status: 'approved',
    });
    notifications.show({
      title: 'Intro approved',
      message: 'Queued for extension send.',
      color: 'sage',
    });
    if (!navigateAfterQueueAction()) navigate('/v2/board');
  }

  // ─── Render ──
  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn&apos;t load this person</Text>
          <Text size="sm" c="dimmed">{error}</Text>
          <UnstyledButton onClick={goBack}><Text c="sage">← Back to {back.label}</Text></UnstyledButton>
        </Stack>
      </Center>
    );
  }
  if (!lead) return <Center py={80}><Loader color="sage" /></Center>;

  const leadLocation = [lead.city, lead.country].filter(Boolean).join(', ')
    || String(lead.address || '').trim();
  const subLine = [lead.title, lead.company, leadLocation].filter(Boolean).join(' · ');
  const linkedin = String(lead.linkedinUrl || lead.socials?.linkedin || '').trim();
  const email = String(lead.bestEmail || lead.email || '').trim();
  const website = String(lead.website || '').trim();
  // fitScore is the AI 0-100 fit. Fall back to null (not readiness) so we hide
  // the circle when we don't have a real fit value (rather than showing "2").
  const fitScore = typeof lead.fitScore === 'number' && lead.fitScore >= 10 ? lead.fitScore : 0;
  const stageLabel = stagePillLabel(lead.stage);
  const isBooked = String(lead.stage || '').toLowerCase() === 'booked';
  const whyText = String(lead.coachNote || lead.fitReason || '').trim();
  const touchInfo = touches.length ? `touch 1 of ${touches.length}` : '';

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 1200, margin: '0 auto' }}>
      <Stack gap="lg">
        {/* Breadcrumb */}
        <UnstyledButton onClick={goBack} style={{ alignSelf: 'flex-start' }}>
          <Group gap={6} align="center">
            <IconArrowLeft size={14} style={{ color: 'var(--aviary-muted)' }} />
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{back.label}</Text>
            <Text size="sm" style={{ color: 'var(--aviary-disabled)' }}>/</Text>
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{stageLabel}</Text>
          </Group>
        </UnstyledButton>

        {/* Header */}
        <Group align="flex-start" wrap="nowrap" gap="md">
          <Avatar src={lead.photoUrl || null} size={72} radius="xl" color="sage">
            {initialsFor(lead.name)}
          </Avatar>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} size="xl" style={{ color: 'var(--aviary-ink)', lineHeight: 1.2 }}>
              {lead.name || '(no name)'}
            </Text>
            {subLine && <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>{subLine}</Text>}
            <Group gap={8} mt={2}>
              {linkedin && <ContactChip Icon={IconBrandLinkedin} label="in ↗" href={linkedin} />}
              {email && <ContactChip Icon={IconMail} label="email" href={`mailto:${email}`} />}
              {website && (
                <ContactChip
                  Icon={IconExternalLink}
                  label={domainOf(website)}
                  href={website.startsWith('http') ? website : `https://${website}`}
                />
              )}
            </Group>
          </Stack>
          <FitCircle score={fitScore} />
        </Group>

        {/* Stage row */}
        <Group justify="space-between" align="center" wrap="wrap">
          <Stack gap={4}>
            <Group gap="sm" align="center">
              <Box
                style={{
                  padding: '4px 12px',
                  borderRadius: 999,
                  border: '1.5px solid var(--mantine-color-sage-6)',
                  color: 'var(--mantine-color-sage-6)',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {stageLabel}
              </Box>
              {campaign && !queueMode && (
                <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                  {campaign.name}{touchInfo ? ` · ${touchInfo}` : ''}
                </Text>
              )}
            </Group>
            {queueMode && queueCount > 0 && (
              <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
                {queueLabel} · {Math.max(1, queueIndex + 1)} of {queueCount} to contact
              </Text>
            )}
          </Stack>
          <Group gap="sm" align="center">
            {queueMode && (
              <Group gap={6}>
                <Button
                  variant="default"
                  size="xs"
                  disabled={!prevQueuedLead}
                  leftSection={<IconArrowLeft size={14} />}
                  onClick={() => goToQueuedLead(prevQueuedLead)}
                >
                  prev
                </Button>
                <Button
                  variant="default"
                  size="xs"
                  disabled={!nextQueuedLead}
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => goToQueuedLead(nextQueuedLead)}
                >
                  next
                </Button>
              </Group>
            )}
            {!isBooked && !queueMode && (
              <UnstyledButton onClick={handleMoveToBooked} disabled={movingToBooked}>
                <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-sage-6)', opacity: movingToBooked ? 0.6 : 1 }}>
                  {movingToBooked ? 'Moving…' : 'Move to Booked'}
                </Text>
              </UnstyledButton>
            )}
          </Group>
        </Group>

        {/* Two-column grid: tabs + right sidebar */}
        <Group align="flex-start" wrap="nowrap" gap="lg">
          {/* LEFT: tabs */}
          <Stack gap="md" style={{ flex: 3, minWidth: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              color="sage"
              variant="pills"
              radius="xl"
              styles={SEGMENTED_TABS_STYLES}
            >
              <Tabs.List>
                {isConnected ? (
                  <>
                    <Tabs.Tab value="message" leftSection={<IconMessage size={14} />}>Message</Tabs.Tab>
                    {introTabVisible && (
                      <Tabs.Tab
                        value="intro"
                        leftSection={<IconMessage size={14} />}
                        rightSection={shouldProposeIntro
                          ? <Badge size="xs" color="yellow" variant="light">1 pending</Badge>
                          : null}
                      >
                        Intro
                      </Tabs.Tab>
                    )}
                    {articleTabVisible && (
                      <Tabs.Tab
                        value="article"
                        leftSection={<IconMessage size={14} />}
                      >
                        Article
                      </Tabs.Tab>
                    )}
                    <Tabs.Tab
                      value="activity"
                      leftSection={<IconEye size={14} />}
                    >
                      Activity
                    </Tabs.Tab>
                    <Tabs.Tab
                      value="plan"
                      leftSection={<IconEye size={14} />}
                    >
                      Plan
                    </Tabs.Tab>
                  </>
                ) : (
                  <>
                    <Tabs.Tab
                      value="message"
                      leftSection={<IconMessage size={14} />}
                      rightSection={pendingCount > 0
                        ? <Badge size="xs" color="yellow" variant="light">{pendingCount} pending</Badge>
                        : null}
                    >
                      Message
                    </Tabs.Tab>
                    <Tabs.Tab value="activity" leftSection={<IconEye size={14} />}>Activity</Tabs.Tab>
                    <Tabs.Tab value="plan" leftSection={<IconEye size={14} />}>Plan</Tabs.Tab>
                  </>
                )}
              </Tabs.List>

              {isConnected && introTabVisible && (
                <Tabs.Panel value="intro" pt="md">
                  {latestIntroDraft?.sentAt || latestIntroDraft?.approvedAt ? (
                    /* Sent (or approved and queued) intro — read-only informational. */
                    <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
                      <Stack gap="md">
                        <Group justify="space-between" align="baseline">
                          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                            MESSAGE · INTRO
                          </Text>
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                            {latestIntroDraft.sentAt
                              ? `Sent ${formatAgo(latestIntroDraft.sentAt)}`
                              : `Approved ${formatAgo(latestIntroDraft.approvedAt)} · queued for send`}
                          </Text>
                        </Group>
                        <Box
                          style={{
                            background: 'var(--aviary-surface)',
                            border: '1px solid var(--aviary-disabled)',
                            borderRadius: 10,
                            padding: 14,
                            fontSize: 14.5,
                            lineHeight: 1.55,
                            color: 'var(--aviary-ink)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {String(latestIntroDraft.messageBody || '').trim()}
                        </Box>
                      </Stack>
                    </Paper>
                  ) : (latestIntroDraft || introDraftBusy || shouldProposeIntro) ? (
                    /* Pending (or generating / skipped-but-regenerable) intro — editable. */
                    <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
                      <Stack gap="md">
                        <Group justify="space-between" align="baseline">
                          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                            MESSAGE · INTRO
                          </Text>
                          <Button
                            variant="subtle"
                            size="compact-sm"
                            color="sage"
                            leftSection={<IconRefresh size={12} />}
                            loading={introDraftBusy}
                            disabled={introDraftBusy}
                            onClick={handleRewriteIntro}
                          >
                            rewrite with AI
                          </Button>
                        </Group>
                        <Textarea
                          value={introDraftBody}
                          onChange={(e) => setIntroDraftBody(e.currentTarget.value)}
                          autosize
                          minRows={5}
                          disabled={introDraftBusy}
                          placeholder="Generating intro message..."
                          styles={{
                            input: {
                              background: 'var(--aviary-surface)',
                              border: '1px solid var(--aviary-disabled)',
                              borderRadius: 10,
                              padding: 14,
                              fontSize: 14.5,
                              lineHeight: 1.55,
                              color: 'var(--aviary-ink)',
                            },
                          }}
                        />
                        <Group gap="sm" justify="space-between" wrap="nowrap">
                          <Button
                            variant="subtle"
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={handleDeleteLead}
                            disabled={introDraftBusy}
                          >
                            Delete
                          </Button>
                          <Group gap="sm" wrap="nowrap">
                            <Button
                              variant="default"
                              color="gray"
                              onClick={handleSkipIntroDraft}
                              disabled={introDraftBusy}
                            >
                              Skip
                            </Button>
                            <Button
                              color="sage"
                              onClick={handleApproveIntroDraft}
                              disabled={introDraftBusy || !introDraftBody.trim()}
                              loading={articleDraftBusy}
                            >
                              Approve →
                            </Button>
                          </Group>
                        </Group>
                      </Stack>
                    </Paper>
                  ) : (
                    /* No intro at all — rare edge (e.g. generation failed). */
                    <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
                      <Text size="sm" ta="center" style={{ color: 'var(--aviary-muted)' }}>
                        No intro sent to {firstNameOf(lead.name) || 'this lead'} yet.
                      </Text>
                    </Paper>
                  )}
                </Tabs.Panel>
              )}

              {isConnected && (
                <Tabs.Panel value="article" pt="md">
                  <Stack gap="md">
                    {activeArticleDraft ? (
                      <Box ref={articleDraftRef}>
                        <Stack gap={6}>
                          <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                            For: {activeArticleDraft.articleTitle || activeArticleDraft.articleSlug}
                          </Text>
                          <ArticleDraftEditor
                            key={activeArticleDraft.id}
                            draft={activeArticleDraft}
                            onApprove={handleApproveArticleDraft}
                            onSkip={handleSkipArticleDraft}
                            onDelete={handleDeleteLead}
                            busy={articleDraftBusy}
                            approveLabel={connectedLaneMode === 'semi' ? 'Approve →' : 'Send article →'}
                          />
                        </Stack>
                      </Box>
                    ) : approvedUnsentArticleDraft && isManualLinkedin ? (
                      <Box ref={articleDraftRef}>
                        <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
                          <Stack gap="md">
                            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                              For: {approvedUnsentArticleDraft.articleTitle || approvedUnsentArticleDraft.articleSlug}
                            </Text>
                            <Group justify="space-between" align="baseline">
                              <Group gap="xs" align="baseline">
                                <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                                  MESSAGE · APPROVED
                                </Text>
                                <Text size="xs" style={{ color: 'var(--mantine-color-sage-7)' }}>
                                  {approvedUnsentArticleDraft.articleTitle || approvedUnsentArticleDraft.articleSlug}
                                </Text>
                              </Group>
                              <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                                Approved {formatAgo(approvedUnsentArticleDraft.approvedAt)} · queued for send
                              </Text>
                            </Group>
                            <Box
                              style={{
                                padding: 14,
                                background: 'var(--aviary-surface)',
                                border: '1px solid var(--aviary-disabled)',
                                borderRadius: 10,
                                fontSize: 14.5,
                                lineHeight: 1.55,
                                color: 'var(--aviary-ink)',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(approvedUnsentArticleDraft.messageBody || '')}
                            </Box>
                            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                              Click Approve to re-queue this send for the Aviary Assistant Chrome extension.
                            </Text>
                            <Group justify="flex-end">
                              <Button
                                color="sage"
                                onClick={() => handleRequeueArticleDraft(approvedUnsentArticleDraft)}
                                disabled={articleDraftBusy}
                                loading={articleDraftBusy}
                              >
                                Approve
                              </Button>
                            </Group>
                          </Stack>
                        </Paper>
                      </Box>
                    ) : (!isCoachFlow ? (
                      <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
                        <Text size="sm" ta="center" style={{ color: 'var(--aviary-muted)' }}>
                          No article queued for {firstNameOf(lead.name) || 'this lead'}. Share one from the Board or the Articles page.
                        </Text>
                      </Paper>
                    ) : null)}

                    {isCoachFlow && (
                      articleTableRows.length ? (
                        <Paper p="md" radius="md" withBorder style={{ background: '#fff' }}>
                          <Stack gap="sm">
                            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                              AVAILABLE ARTICLES
                            </Text>
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Title</Table.Th>
                                  <Table.Th>Status</Table.Th>
                                  <Table.Th>Stats</Table.Th>
                                  <Table.Th style={{ width: 140 }}>Action</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {articleTableRows.map((row) => {
                                  const article = row.article || {};
                                  const rowIsSending = articleSendSlug === article.slug;
                                  return (
                                    <Table.Tr
                                      key={article.slug}
                                      style={row.status === 'sent' ? { opacity: 0.6 } : undefined}
                                    >
                                      <Table.Td>
                                        <Group gap="xs" wrap="wrap">
                                          <Text size="sm" fw={500}>{article.title || article.slug}</Text>
                                          {String(article.kind || '').toLowerCase() === 'external' && (
                                            <Badge size="xs" variant="light" color="gray">External</Badge>
                                          )}
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        {row.status === 'available' && <Badge size="xs" color="sage" variant="light">Available</Badge>}
                                        {row.status === 'queued' && <Badge size="xs" color="yellow" variant="light">Queued</Badge>}
                                        {row.status === 'sent' && (
                                          <Badge size="xs" color="gray" variant="light">
                                            Sent {formatAgo(row.sentAt)}
                                          </Badge>
                                        )}
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
                                          {row.status === 'sent' ? `${row.opens} opens · ${row.reads} reads` : '—'}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        {row.status === 'available' && (
                                          <Button
                                            size="xs"
                                            color="sage"
                                            loading={rowIsSending}
                                            disabled={Boolean(articleSendSlug) || articleDraftBusy}
                                            onClick={() => handleShareArticle(article)}
                                          >
                                            Send
                                          </Button>
                                        )}
                                        {row.status === 'queued' && (
                                          <UnstyledButton onClick={handleOpenQueuedDraft}>
                                            <Text size="xs" fw={600} style={{ color: 'var(--mantine-color-sage-7)' }}>
                                              Open draft
                                            </Text>
                                          </UnstyledButton>
                                        )}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          </Stack>
                        </Paper>
                      ) : (
                        !activeArticleDraft && !approvedUnsentArticleDraft && (
                          <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
                            <Text size="sm" ta="center" style={{ color: 'var(--aviary-muted)' }}>
                              No published articles yet. Create one from the Articles page.
                            </Text>
                          </Paper>
                        )
                      )
                    )}
                  </Stack>
                </Tabs.Panel>
              )}

              {/* Message tab — pure conversation flow (chat bubbles only, no
                  system events or editor controls). Shown for every lead so
                  Jackie can always follow the exchange without interruption. */}
              <Tabs.Panel value="message" pt="md">
                <MessageFlowCard entries={timeline} />
              </Tabs.Panel>

              <Tabs.Panel value="activity" pt="md">
                <TimelineCard entries={timeline} />
              </Tabs.Panel>

              <Tabs.Panel value="plan" pt="md">
                <PlanCard plan={plan} />
              </Tabs.Panel>
            </Tabs>
          </Stack>

          {/* RIGHT: WHY HIM + coaching notes (persistent across tabs) */}
          <Stack gap="md" style={{ flex: 2, minWidth: 260 }}>
            {whyText && (
              <Paper p="lg" radius="md" withBorder style={{ background: '#fff' }}>
                <Stack gap="md">
                  <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                    WHY {firstNameOf(lead.name)?.toUpperCase() || 'THIS ONE'}
                  </Text>
                  <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55 }}>
                    {whyText}
                  </Text>
                </Stack>
              </Paper>
            )}

            <Stack gap={6}>
              <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
                COACHING NOTES
              </Text>
              <Stack gap="xs">
                {coachingNotes.map((note) => (
                  <Box
                    key={note.id}
                    style={{
                      border: '1px solid var(--mantine-color-sage-4)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      background: '#fff',
                    }}
                  >
                    <Text size="sm" style={{ color: 'var(--aviary-ink)', whiteSpace: 'pre-wrap' }}>
                      {note.body}
                    </Text>
                    <Text size="xs" style={{ color: 'var(--aviary-muted)', marginTop: 6 }}>
                      — {note.author || 'you'} · {formatAgo(note.createdAt)}
                    </Text>
                  </Box>
                ))}
              </Stack>
              <Box style={{ position: 'relative' }}>
                <Textarea
                  value={newNoteBody}
                  onChange={(e) => setNewNoteBody(e.currentTarget.value)}
                  autosize
                  minRows={3}
                  placeholder="+ Add a coaching note..."
                  styles={{
                    input: {
                      background: '#fff',
                      border: '1px solid var(--mantine-color-sage-5)',
                      borderRadius: 10,
                      fontSize: 14,
                      lineHeight: 1.55,
                      color: 'var(--aviary-ink)',
                      paddingBottom: newNoteBody.trim() ? 48 : 12,
                    },
                  }}
                  classNames={{ input: 'v2-coaching-note-input' }}
                />
                <style>{`
                  .v2-coaching-note-input::placeholder {
                    color: var(--mantine-color-sage-7) !important;
                    opacity: 1;
                    font-weight: 500;
                  }
                `}</style>
                {newNoteBody.trim() && (
                  <Button
                    color="sage"
                    size="xs"
                    onClick={handleSaveNotes}
                    loading={notesSaving}
                    disabled={notesSaving}
                    style={{ position: 'absolute', right: 10, bottom: 10 }}
                  >
                    Save
                  </Button>
                )}
              </Box>
            </Stack>
          </Stack>
        </Group>

        {queueMode && (
          <>
            <Divider mt="xl" variant="dashed" />
            <Group justify="center">
              <UnstyledButton onClick={() => navigate('/v2/board')}>
                <Text size="xs" c="dimmed">Back to Board</Text>
              </UnstyledButton>
            </Group>
          </>
        )}
      </Stack>
    </Box>
  );
}
